import {makeAutoObservable, observable} from "mobx";
import {ViewController} from "data/types/structure";
import {injectable, inject} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {GameLevel, ModalType} from "data/enums";
import type {IGameBarStore} from "data/stores/game_bar/game_bar.store";
import type {IContestsStore} from "data/stores/contests/contestsStore";
import {useRef, RefObject} from "react";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {extractErrorMessage} from "data/utils";

export enum ScrollButtonsState {
	LEFT,
	USER,
	RIGHT,
	IDLE,
}

interface ILeague {
	level: GameLevel;
	points: number;
	disabled: boolean;
	showPoints: boolean;
}

export interface IRoundBadgesController extends ViewController {
	i18n: ILocalizationStore;
	scrollRef: RefObject<HTMLDivElement>;

	get leagues(): ILeague[];
	get hasScroll(): boolean;
	get isLeftDisabled(): boolean;
	get isRightDisabled(): boolean;
	get toPromotionPoints(): number;
	get toDemotionPoints(): number;
	get currentWeeklyPoints(): number;
	get isPromoted(): boolean;

	scrollTo: (state: ScrollButtonsState) => void;
	getActiveClassName: (league: ILeague) => string;
}

@injectable()
export class RoundBadgesController implements IRoundBadgesController {
	public scrollRef = useRef<HTMLDivElement>(null);
	@observable private _scrollState = ScrollButtonsState.LEFT;

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.GameBarStore) public readonly _gameBarStore: IGameBarStore,
		@inject(Bindings.ContestsStore) public readonly _contestsStore: IContestsStore,
		@inject(Bindings.ModalsStore) public readonly _modalsStore: IModalsStore
	) {
		makeAutoObservable(this);
	}

	get userLevel() {
		return this._gameBarStore.gameBar?.level || GameLevel.SINGLE_A;
	}

	get leagues(): ILeague[] {
		const currentLevel = this.userLevel;
		const currentLevelIndex = this.leaguesLevels.findIndex((it) => it.level === currentLevel);

		return this.leaguesLevels.map((it, index) => {
			const isCurrent = index === currentLevelIndex && index !== 0;
			const isNext = index === currentLevelIndex + 1;

			return {
				...it,
				disabled: index > currentLevelIndex,
				showPoints: isCurrent || isNext,
			};
		});
	}

	get hasScroll(): boolean {
		return this._scrollState !== ScrollButtonsState.IDLE;
	}

	get isRightDisabled(): boolean {
		return this._scrollState === ScrollButtonsState.RIGHT;
	}

	get isLeftDisabled(): boolean {
		return this._scrollState === ScrollButtonsState.LEFT;
	}

	get leaguesLevels() {
		return [
			{
				level: GameLevel.SINGLE_A,
				points: 0,
			},
			{
				level: GameLevel.HIGH_A,
				points: 50,
			},
			{
				level: GameLevel.AA,
				points: 100,
			},
			{
				level: GameLevel.AAA,
				points: 150,
			},
			{
				level: GameLevel.MLB_CALL_UP,
				points: 200,
			},
			{
				level: GameLevel.MLB_STARTER,
				points: 250,
			},
			{
				level: GameLevel.MLB_ALL_STAR,
				points: 300,
			},
			{
				level: GameLevel.MLB_HALL_OF_FAMER,
				points: 350,
			},
		];
	}

	get toPromotionPoints() {
		const points = this.currentWeeklyPoints;
		const levels = this.leaguesLevels;
		const levelIndex = levels.findIndex((it) => it.level === this.userLevel);
		const nextLevel = levels[levelIndex + 1];

		if (!nextLevel) {
			return 0;
		}

		return nextLevel.points - points;
	}

	get toDemotionPoints() {
		const points = this.currentWeeklyPoints;
		const level = this.leaguesLevels.find((it) => it.level === this.userLevel);

		if (!level || this.userLevel === GameLevel.SINGLE_A) {
			return 0;
		}

		const toPreventPoints = level.points - points;
		return toPreventPoints < 0 ? 0 : toPreventPoints;
	}

	get isPromoted() {
		const points = this.toPromotionPoints;

		return points <= 0;
	}

	get currentWeeklyPoints() {
		return this._gameBarStore?.gameBar?.roundPoints ?? 0;
	}

	init(param: void) {
		this.fetchGameBar();
		this.checkScrollButtons();
		const scrollElement = this.scrollRef.current;
		scrollElement?.addEventListener("scroll", this.checkScrollButtons);

		this.scrollTo(ScrollButtonsState.USER);
	}

	dispose() {
		const scrollElement = this.scrollRef.current;
		scrollElement?.removeEventListener("scroll", this.checkScrollButtons);
	}

	checkScrollButtons = () => {
		const scrollElement = this.scrollRef.current;
		if (scrollElement) {
			if (scrollElement.scrollWidth === scrollElement.clientWidth) {
				this._scrollState = ScrollButtonsState.IDLE;
				return;
			}

			if (scrollElement.scrollLeft < 10) {
				this._scrollState = ScrollButtonsState.LEFT;
				return;
			}

			const rightScroll =
				scrollElement.scrollWidth - scrollElement.clientWidth - scrollElement.scrollLeft;
			if (rightScroll < 10) {
				this._scrollState = ScrollButtonsState.RIGHT;
				return;
			}

			this._scrollState = ScrollButtonsState.USER;
		}
	};

	scrollTo = (state: ScrollButtonsState) => {
		const scrollElement = this.scrollRef.current;
		if (scrollElement) {
			let scrollValue = 0;
			if (state === ScrollButtonsState.LEFT) {
				scrollValue = scrollElement.scrollLeft - scrollElement.clientWidth;
			}
			if (state === ScrollButtonsState.RIGHT) {
				scrollValue = scrollElement.scrollLeft + scrollElement.clientWidth;
			}

			if (state === ScrollButtonsState.USER) {
				const userLevel = this.userLevel;
				const userBadge = scrollElement.getElementsByClassName(userLevel)[0] as HTMLElement;

				if (userBadge) {
					scrollValue = userBadge.offsetLeft - userBadge.clientWidth - 10;
					scrollElement.scrollTo({
						left: scrollValue,
						behavior: "auto",
					});
					return;
				}
			}

			scrollElement.scrollTo({
				left: scrollValue,
				behavior: "smooth",
			});
		}
	};

	getActiveClassName = (league: ILeague) => {
		return this.userLevel === league.level ? "active" : "";
	};

	private fetchGameBar() {
		const contest = this.contest;
		if (!contest) {
			return;
		}
		void this._gameBarStore.fetchGameBar(contest.id).catch((e) => {
			this._modalsStore.showModal(ModalType.ERROR, {
				message: extractErrorMessage(e),
			});
		});
	}

	private get contest() {
		return this._contestsStore.currentContest;
	}
}
